<template>
    <div v-if="!loading">
        <div
            v-if="slides.length !== 0"
            class="my-2 border-left pl-3"
        >
            <draggable
                v-model="slides"
                handle='.drag-handle'
                :disabled="disabled"
            >

                <slide
                    v-for="item in slides" :key="item.id"
                    :publication="publication"
                    :article="article"
                    :related_media_items="slides"
                    :item="item"
                    :unit="unit"
                    :disabled="disabled"
                    class="mb-2"
                    @deleted="deleteSlide(item.id)"
                    :slide-number="numbering ? slideNumberingMap[item.id] : null"
                />
            </draggable>
        </div>
        <b-alert
            v-else
            variant="danger"
            class="my-2"
            show
        >
            No slides in this walkthrough. Use the 'Add' buttons above to add slides.
        </b-alert>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import moment from 'moment'
import Slide from '@/components/article/slides/Slide'

const slideOrderMapper = (slide, index) => ({...slide, order_number: index + 1})

export default {
    data: () => ({
        loading: false,
    }),
    props: [
        'publication',
        'unit',
        'article',
        'disabled',
        'numbering',
    ],
    components: {
        Slide,
        draggable,
    },
    computed: {
        slides: {
            get() {
                return this.$store.getters.slidesByWalkthroughId(this.article.id)
            },
            async set(slides) {
                // Ensure slide numbers are ordered sequentially after a slide has been moved.
                try {
                    const walkthroughId = this.article.id
                    const updatedSlides = slides.map(slideOrderMapper)
                    this.$store.commit('setWalkthroughSlides', {walkthroughId, slides: updatedSlides})
                    await this.$store.dispatch('updateSlidePositions', {slides: updatedSlides})
                } catch (e) {
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Failed to re-order slides: ${e.message}`,
                        },
                        time: moment().toISOString(),
                    })
                }
            },
        },
        slideNumberingMap() {
            const slides = this.slides.filter((slide) => slide.teacher_only !== true)
            const slideCounter = (slideMap, slide, index) => {
                const { id } = slide
                slideMap[id] = index + 1

                return slideMap
            }

            return slides.reduce(slideCounter, {})
        },
    },
    methods: {
        async deleteSlide(slideId) {
            try {
                const walkthroughId = this.article.id
                // This is invoked here as events from the slide will not fire once the remove call has been dispatched.
                await this.$store.dispatch('removeWalkthroughSlide', {id: slideId, walkthroughId})

                // Ensure slide numbers are ordered sequentially after a slide has been deleted.
                const updatedSlides = this.slides.map(slideOrderMapper)
                this.$store.commit('setWalkthroughSlides', {walkthroughId, slides: updatedSlides})
                await this.$store.dispatch('updateSlidePositions', {walkthroughId, slides: updatedSlides})
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to remove slide: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
    },
    async mounted() {
        // Ensure the list is empty before retrieving the slides.
        // This avoids a repaint which triggers a type error from within TipTap (used by Question slides).
        if (this.slides.length === 0) {
            try {
                this.loading = true
                await this.$store.dispatch('getWalkthroughSlides', this.article.id)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to retrieve slides: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading = false
            }
        }
    },
}
</script>
